import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Folder, File, Link2, Copy, Check, Trash2, ChevronRight, Loader2, Clock, Eye, ExternalLink, AlertTriangle, Download, Globe, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Header } from '@/components/Header';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { fileShare, parseUserAgent, type FileEntry, type LinkToken } from '@/api/file-share';
import { toast } from 'sonner';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

type Tab = 'browse' | 'links';

export function FileSharePage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('browse');
  const [currentPath, setCurrentPath] = useState('/');
  const [shareSheet, setShareSheet] = useState<{ path: string; name: string; isDir: boolean } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [selectedLink, setSelectedLink] = useState<LinkToken | null>(null);

  // File browser
  const { data: filesData, isLoading: filesLoading } = useQuery({
    queryKey: ['file-share-files', currentPath],
    queryFn: () => fileShare.listFiles(currentPath),
    enabled: tab === 'browse' && fileShare.isConfigured(),
  });

  // Active links
  const { data: linksData, isLoading: linksLoading } = useQuery({
    queryKey: ['file-share-links'],
    queryFn: () => fileShare.listLinks(),
    enabled: tab === 'links' && fileShare.isConfigured(),
  });

  const deleteMutation = useMutation({
    mutationFn: (token: string) => fileShare.deleteLink(token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['file-share-links'] });
      toast.success('Ссылка удалена');
    },
    onError: () => toast.error('Ошибка удаления'),
  });

  const navigateTo = useCallback((entry: FileEntry) => {
    if (entry.is_dir) {
      setCurrentPath((prev) => (prev === '/' ? '/' + entry.name : prev + '/' + entry.name));
    }
  }, []);

  const navigateUp = useCallback(() => {
    setCurrentPath((prev) => {
      const parts = prev.split('/').filter(Boolean);
      parts.pop();
      return '/' + parts.join('/');
    });
  }, []);

  const handleCopyLink = async (url: string, token: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      toast.success('Ссылка скопирована');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.info(url);
    }
  };

  if (!fileShare.isConfigured()) {
    return (
      <div className="min-h-screen bg-background">
        <Header title="Файлы" />
        <div className="p-4 text-center text-muted-foreground">
          Сервис не настроен (VITE_FILE_SHARE_URL)
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header title="Файлы" />

      {/* Tabs */}
      <div className="flex border-b px-4">
        <button
          className={`flex-1 py-2.5 text-sm font-medium text-center border-b-2 transition-colors ${
            tab === 'browse' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
          }`}
          onClick={() => setTab('browse')}
        >
          Обзор файлов
        </button>
        <button
          className={`flex-1 py-2.5 text-sm font-medium text-center border-b-2 transition-colors ${
            tab === 'links' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
          }`}
          onClick={() => setTab('links')}
        >
          Ссылки
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Browse tab */}
        {tab === 'browse' && (
          <div className="p-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3 overflow-x-auto">
              <button onClick={() => setCurrentPath('/')} className="hover:text-foreground shrink-0">/</button>
              {currentPath.split('/').filter(Boolean).map((part, i, arr) => (
                <span key={i} className="flex items-center gap-1 shrink-0">
                  <ChevronRight className="w-3 h-3" />
                  <button
                    onClick={() => setCurrentPath('/' + arr.slice(0, i + 1).join('/'))}
                    className="hover:text-foreground"
                  >
                    {part}
                  </button>
                </span>
              ))}
            </div>

            {/* Back button */}
            {currentPath !== '/' && (
              <button
                onClick={navigateUp}
                className="flex items-center gap-2 text-sm text-muted-foreground mb-2 hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" /> Назад
              </button>
            )}

            {filesLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Загрузка...
              </div>
            ) : (
              <div className="space-y-1">
                {filesData?.entries?.map((entry) => (
                  <div
                    key={entry.name}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card active:scale-[0.99] transition-transform"
                  >
                    <button
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      onClick={() => navigateTo(entry)}
                      disabled={!entry.is_dir}
                    >
                      {entry.is_dir ? (
                        <Folder className="w-5 h-5 text-blue-500 shrink-0" />
                      ) : (
                        <File className="w-5 h-5 text-gray-400 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{entry.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {!entry.is_dir && formatBytes(entry.size)}
                          {entry.is_dir && 'Папка'}
                        </div>
                      </div>
                      {entry.is_dir && <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-8 w-8"
                      onClick={() => setShareSheet({
                        path: currentPath === '/' ? '/' + entry.name : currentPath + '/' + entry.name,
                        name: entry.name,
                        isDir: entry.is_dir,
                      })}
                    >
                      <Link2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {filesData?.entries?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">Пустая папка</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Links tab */}
        {tab === 'links' && (
          <div className="p-4 space-y-2">
            {linksLoading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Загрузка...
              </div>
            ) : (linksData?.links || []).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">Нет ссылок</div>
            ) : (
              (linksData?.links || []).map((link) => {
                const isExpired = new Date(link.expires_at) < new Date();
                const isExhausted = link.max_downloads > 0 && link.download_count >= link.max_downloads;
                const url = (import.meta.env.VITE_FILE_SHARE_URL || '') + '/dl/' + link.token;

                return (
                  <div
                    key={link.token}
                    className={`p-3 rounded-lg border bg-card active:scale-[0.99] transition-transform ${isExpired || isExhausted ? 'opacity-50' : ''}`}
                    onClick={() => setSelectedLink(link)}
                  >
                    <div className="flex items-start gap-2">
                      <Link2 className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{link.path}</div>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-0.5">
                            <Clock className="w-3 h-3" />
                            {isExpired ? 'Истекла' : formatDate(link.expires_at)}
                          </span>
                          <span className="flex items-center gap-0.5">
                            <Eye className="w-3 h-3" />
                            {link.download_count}{link.max_downloads > 0 ? `/${link.max_downloads}` : ''}
                          </span>
                          {link.note && <span className="italic">"{link.note}"</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {!isExpired && !isExhausted && (
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => handleCopyLink(url, link.token)}
                          >
                            {copied === link.token ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500"
                          onClick={() => deleteMutation.mutate(link.token)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Share sheet */}
      <CreateShareSheet
        open={!!shareSheet}
        onOpenChange={(open) => !open && setShareSheet(null)}
        path={shareSheet?.path || ''}
        name={shareSheet?.name || ''}
        isDir={shareSheet?.isDir || false}
        onCreated={(url) => {
          handleCopyLink(url, 'new');
          setShareSheet(null);
          queryClient.invalidateQueries({ queryKey: ['file-share-links'] });
        }}
      />

      {/* Stats sheet */}
      <LinkStatsSheet
        link={selectedLink}
        open={!!selectedLink}
        onOpenChange={(open) => !open && setSelectedLink(null)}
      />
    </div>
  );
}

// Sheet for creating a share link
function CreateShareSheet({ open, onOpenChange, path, name, isDir, onCreated }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  path: string;
  name: string;
  isDir: boolean;
  onCreated: (url: string) => void;
}) {
  const [ttlDays, setTtlDays] = useState(3);
  const [maxDownloads, setMaxDownloads] = useState(10);
  const [note, setNote] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const result = await fileShare.createLink({
        path,
        ttl_minutes: ttlDays * 1440,
        max_downloads: maxDownloads,
        note,
      });
      setResultUrl(result.url);
    } catch (err: unknown) {
      toast.error('Ошибка создания ссылки');
    } finally {
      setIsCreating(false);
    }
  };

  // Reset form when sheet opens or target changes
  useEffect(() => {
    if (open) {
      setResultUrl(null);
      setNote('');
      setTtlDays(3);
      setMaxDownloads(10);
      setIsCreating(false);
    }
  }, [open, path]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-auto max-h-[70vh]">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Создать ссылку
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {!resultUrl ? (
            <>
              <div className="rounded-lg bg-muted p-3 text-sm">
                <div className="flex items-center gap-2">
                  {isDir ? <Folder className="w-4 h-4 text-blue-500" /> : <File className="w-4 h-4 text-gray-400" />}
                  <span className="font-medium truncate">{name}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{path}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Срок (дней)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="range"
                      min={1} max={7} value={ttlDays}
                      onChange={(e) => setTtlDays(parseInt(e.target.value))}
                      className="flex-1 accent-primary"
                    />
                    <span className="text-sm font-medium w-6 text-center">{ttlDays}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Макс. скачиваний</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="range"
                      min={1} max={10} value={maxDownloads}
                      onChange={(e) => setMaxDownloads(parseInt(e.target.value))}
                      className="flex-1 accent-primary"
                    />
                    <span className="text-sm font-medium w-6 text-center">{maxDownloads}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground">Заметка (необязательно)</label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Для кого ссылка..." className="mt-1" />
              </div>

              <Button onClick={handleCreate} disabled={isCreating} className="w-full">
                {isCreating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Создание...</> : <><Link2 className="w-4 h-4 mr-2" /> Создать ссылку</>}
              </Button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 p-3">
                <div className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">Ссылка создана!</div>
                <div className="text-xs font-mono break-all bg-white dark:bg-black/20 rounded p-2 border">
                  {resultUrl}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => {
                  onCreated(resultUrl);
                }}>
                  <Copy className="w-4 h-4 mr-2" /> Копировать
                </Button>
                {typeof navigator !== 'undefined' && 'share' in navigator && (
                  <Button variant="outline" className="flex-1" onClick={() => {
                    navigator.share({ title: name, url: resultUrl }).catch(() => {});
                  }}>
                    <ExternalLink className="w-4 h-4 mr-2" /> Поделиться
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function formatDownloadMeta(metaJson: string): string {
  try {
    const meta = JSON.parse(metaJson);
    const type = meta.download_type;
    if (type === 'single') return `${meta.file_path || meta.file_names?.[0] || 'файл'}`;
    if (type === 'archive_all') return 'Архив (все файлы)';
    if (type === 'archive_selected') {
      if (meta.file_names) return `Архив: ${meta.file_names.join(', ')}`;
      if (meta.file_count) return `Архив: ${meta.file_count} файлов`;
    }
    if (meta.file_path) return meta.file_path;
    if (meta.error) return meta.error;
    if (meta.reason) return meta.reason === 'expired' ? 'Ссылка истекла' : 'Лимит скачиваний';
    return '';
  } catch {
    return '';
  }
}

// Sheet for viewing download stats of a link
function LinkStatsSheet({ link, open, onOpenChange }: {
  link: LinkToken | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['file-share-audit', link?.token],
    queryFn: () => fileShare.getAuditEvents(link!.token),
    enabled: open && !!link,
  });

  const events = (data?.events || []).filter(
    (e) => e.event === 'file_downloaded' || e.event === 'stream_error' || e.event === 'download_rejected'
  );

  const isExpired = link ? new Date(link.expires_at) < new Date() : false;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80vh] flex flex-col">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2 text-base">
            <Eye className="w-5 h-5" />
            Статистика
          </SheetTitle>
        </SheetHeader>

        {link && (
          <div className="space-y-1 pb-3 border-b">
            <div className="text-sm font-medium truncate">{link.path}</div>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-0.5">
                <Clock className="w-3 h-3" />
                {isExpired ? 'Истекла' : formatDate(link.expires_at)}
              </span>
              <span className="flex items-center gap-0.5">
                <Download className="w-3 h-3" />
                {link.download_count}{link.max_downloads > 0 ? `/${link.max_downloads}` : ''}
              </span>
              {link.note && <span className="italic">"{link.note}"</span>}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto py-3 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Загрузка...
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Ещё никто не скачивал
            </div>
          ) : (
            events.map((event) => {
              const isError = event.event === 'stream_error';
              const isRejected = event.event === 'download_rejected';
              const detail = formatDownloadMeta(event.meta);

              return (
                <div
                  key={event.id}
                  className={`p-2.5 rounded-lg border text-xs ${
                    isError ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950' :
                    isRejected ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-950' :
                    'bg-card'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 font-medium">
                      {isError ? (
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                      ) : isRejected ? (
                        <Ban className="w-3 h-3 text-yellow-600" />
                      ) : (
                        <Globe className="w-3 h-3 text-muted-foreground" />
                      )}
                      {parseUserAgent(event.user_agent)}
                    </span>
                    <span className="text-muted-foreground shrink-0">
                      {formatDate(event.created_at)}
                    </span>
                  </div>
                  <div className="text-muted-foreground mt-0.5 pl-[18px]">
                    {event.remote_ip}
                  </div>
                  {detail && (
                    <div className={`mt-0.5 pl-[18px] ${isError ? 'text-red-600 dark:text-red-400' : isRejected ? 'text-yellow-700 dark:text-yellow-400' : 'text-foreground'}`}>
                      {detail}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
